import type { ComponentType } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

export interface StepDef<TStepId extends string = string> {
    id: TStepId;
    label: string;
    /** Optional — primary steps render with icons; sub-steps usually skip them. */
    icon?: ComponentType<{ className?: string }>;
}

interface StepHeaderProps<TStepId extends string> {
    steps: readonly StepDef<TStepId>[];
    activeId: TStepId;
    /** Per-step completion state — used to render the green check on past steps. */
    isComplete: (id: TStepId) => boolean;
    /** Map step id → enabled-for-jump. Default: every step is reachable. */
    canJumpTo?: (id: TStepId) => boolean;
    onJumpTo: (id: TStepId) => void;
    /**
     * Visual scale.
     *   - 'md' (default): primary step indicator with border-bottom, larger
     *     chips, full padding. Sits at the top of the editor.
     *   - 'sm': sub-tab variant for nested sub-steppers. No border-bottom,
     *     smaller chips, lighter padding. Designed to sit inside a parent
     *     container that provides its own framing.
     */
    size?: 'md' | 'sm';
}

/**
 * Horizontal step chip indicator. Active step is solid dark; past steps are
 * subtle gray; future steps faded. Mirrors the visual language of the RDI
 * wizard so the work-queue feels consistent across clinical documents.
 *
 * Keyboard accessible — every chip is a real `<button>`. Jumps are gated by
 * `canJumpTo` so we can't navigate past an unfinished blocking step.
 */
export function StepHeader<TStepId extends string>({
    steps,
    activeId,
    isComplete,
    canJumpTo,
    onJumpTo,
    size = 'md'
}: StepHeaderProps<TStepId>) {
    const activeIndex = steps.findIndex((s) => s.id === activeId);

    const containerClass =
        size === 'sm'
            ? 'flex items-center justify-center gap-1 px-5 py-2.5 flex-shrink-0 overflow-x-auto scrollbar-hide bg-gray-50/60 border-b border-gray-200'
            : 'flex items-center justify-center gap-1 px-5 py-2.5 border-b border-gray-200 flex-shrink-0 overflow-x-auto scrollbar-hide';

    const chipBaseClass =
        size === 'sm'
            ? 'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed '
            : 'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 disabled:opacity-40 disabled:cursor-not-allowed ';

    const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
    const dividerWidth = size === 'sm' ? 'w-4' : 'w-6';

    return (
        <div className={containerClass}>
            {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.id === activeId;
                const isPast = index < activeIndex;
                const completed = isComplete(step.id);
                const reachable = canJumpTo ? canJumpTo(step.id) : true;

                const chipColorClass = isActive
                    ? 'bg-gray-900 text-white'
                    : isPast || completed
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-gray-700';

                return (
                    <div key={step.id} className="flex items-center flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => onJumpTo(step.id)}
                            disabled={!reachable}
                            aria-current={isActive ? 'step' : undefined}
                            className={chipBaseClass + chipColorClass}
                        >
                            {completed && !isActive ? (
                                <CheckIcon className={`${iconSize} text-emerald-500`} />
                            ) : Icon ? (
                                <Icon className={iconSize} />
                            ) : null}
                            <span>{step.label}</span>
                        </button>
                        {index < steps.length - 1 && (
                            <div
                                className={`${dividerWidth} h-px mx-1 ${isPast ? 'bg-gray-300' : 'bg-gray-200'}`}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
